import { BUILD_PROPERTIES } from '@/editor/constant/buildProperties';
import { IFurniture } from '@/editor/data/model/furniture';
import { EColumnType, IColumnData } from '@/editor/data/model/model';
import { globalStores } from '@/editor/stores';
import { EEntityType, EOperateMode } from '@/editor/types';
import {
  EParamGroupLabel,
  EParamLabel,
  EParamType,
  EParamUnit,
  EParamsKeys,
  IEditParams
} from '@/editor/types/editParamType';
import { IMaterialInfoData } from '@/pages/app/layout/tab-params/types';
import { getLibraryModelBaseInfo } from '../utils';

function getColumnGroupItem(columnData: IColumnData) {
  const isSquare = columnData.type === EColumnType.squareColumn;
  const length = isSquare ? Math.round(columnData.width) : 0;
  const width = isSquare ? Math.round(columnData.thickness) : 0;
  const diameter = !isSquare ? Math.round(columnData.radius * 2) : 0;
  const height = columnData.height;
  const rotation = Math.round((columnData.rotation / Math.PI) * 180);

  const values = {
    [EParamsKeys.length]: length,
    [EParamsKeys.width]: width,
    [EParamsKeys.diameter]: diameter,
    [EParamsKeys.rotation]: rotation,
    [EParamsKeys.height]: height
  };
  const fields: IEditParams['fields'] = isSquare
    ? {
        [EParamsKeys.length]: {
          label: EParamLabel.SECTIONLENGTH,
          type: EParamType.NUMBER,
          unit: EParamUnit.MM,
          settings: {
            readonly: false,
            max: BUILD_PROPERTIES.MAX_INPUT,
            min: BUILD_PROPERTIES.MIN_INPUT
          }
        },
        [EParamsKeys.width]: {
          type: EParamType.NUMBER,
          unit: EParamUnit.MM,
          label: EParamLabel.SECTIONWIDTH,
          settings: {
            readonly: false,
            max: BUILD_PROPERTIES.MAX_INPUT,
            min: BUILD_PROPERTIES.MIN_INPUT
          }
        },
        [EParamsKeys.rotation]: {
          type: EParamType.NUMBER,
          label: EParamLabel.SECTIONROTATION,
          unit: EParamUnit.ANGLE,
          settings: {
            min: BUILD_PROPERTIES.ROTATION_ANGLE_RANGE[0],
            max: BUILD_PROPERTIES.ROTATION_ANGLE_RANGE[1],
            readonly: false
          }
        },
        [EParamsKeys.height]: {
          type: EParamType.NUMBER,
          label: EParamLabel.POSTHEIGHT,
          unit: EParamUnit.MM,
          settings: {
            readonly: true
          }
        }
      }
    : {
        [EParamsKeys.diameter]: {
          type: EParamType.NUMBER,
          unit: EParamUnit.MM,
          label: EParamLabel.SECTIONDIAMETER,
          settings: {
            readonly: false,
            max: BUILD_PROPERTIES.MAX_INPUT,
            min: BUILD_PROPERTIES.MIN_INPUT
          }
        },
        [EParamsKeys.height]: {
          type: EParamType.NUMBER,
          label: EParamLabel.POSTHEIGHT,
          unit: EParamUnit.MM,
          settings: {
            readonly: true
          }
        }
      };
  return {
    values,
    fields
  };
}

export function getColumnEntityParamInfo(columnData: IColumnData, type: EEntityType | EOperateMode) {
  if (columnData) {
    const title =
      type === EOperateMode.DrawStruct
        ? EParamGroupLabel.DRAWCOLUMN
        : columnData.type === EColumnType.squareColumn
          ? EParamGroupLabel.SQUARECOLUMN
          : EParamGroupLabel.CYLINDER;

    const { fields, values } = getColumnGroupItem(columnData);
    const paramDatas: IEditParams = {
      id: columnData.id,
      type: type,
      title: title,
      group:
        columnData.type === EColumnType.squareColumn
          ? [
              {
                id: EParamGroupLabel.SIZE,
                label: EParamGroupLabel.SIZE,
                fields: [EParamsKeys.length, EParamsKeys.width, EParamsKeys.rotation, EParamsKeys.height]
              }
            ]
          : [
              {
                id: EParamGroupLabel.SIZE,
                label: EParamGroupLabel.SIZE,
                fields: [EParamsKeys.diameter, EParamsKeys.height]
              }
            ],
      fields,
      values
    };

    return paramDatas;
  }

  return undefined;
}

export const getFurnitureEntityParamInfo = (furnitureData: IFurniture, virtual = false): IEditParams | undefined => {
  const modelInfo = globalStores.modelStore.getModel(furnitureData.modelId);
  if (!modelInfo) return undefined;
  return {
    id: furnitureData.id,
    type: EEntityType.Furniture,
    title: EParamGroupLabel.EMPTY_LABEL,
    options: {
      hideTitle: true
    },
    values: {
      [EParamsKeys.length]: Math.round(furnitureData.thickness),
      [EParamsKeys.width]: Math.round(furnitureData.width),
      [EParamsKeys.height]: Math.round(furnitureData.height),
      [EParamsKeys.rotation]: Math.round((furnitureData.rotation / Math.PI) * 180),
      [EParamsKeys.offGround]: Math.round(furnitureData.offGround),
      [EParamsKeys.furnitureInfo]: {
        ...getLibraryModelBaseInfo(modelInfo),
        id: furnitureData.id,
        type: EEntityType.Furniture,
        replaceable: !virtual
      } satisfies IMaterialInfoData
    },
    group: [
      {
        id: EParamGroupLabel.EMPTY_LABEL,
        label: EParamGroupLabel.EMPTY_LABEL,
        fields: [EParamsKeys.furnitureInfo],
        hideLabel: true
      },
      {
        id: EParamGroupLabel.SIZE,
        label: EParamGroupLabel.SIZE,
        resettable: true,
        fields: [
          EParamsKeys.width,
          EParamsKeys.length,
          EParamsKeys.height,
          EParamsKeys.rotation,
          EParamsKeys.offGround
        ],
        linkedFields: [EParamsKeys.width, EParamsKeys.length, EParamsKeys.height],
        defaultLinked: true
      }
    ],
    fields: {
      [EParamsKeys.width]: {
        type: EParamType.NUMBER,
        unit: EParamUnit.MM,
        label: EParamLabel.MODEL_WIDTH,
        settings: {
          readonly: false,
          max: BUILD_PROPERTIES.MAX_INPUT,
          min: BUILD_PROPERTIES.MIN_INPUT
        }
      },
      [EParamsKeys.length]: {
        label: EParamLabel.MODEL_LENGTH,
        type: EParamType.NUMBER,
        unit: EParamUnit.MM,
        settings: {
          readonly: false,
          max: BUILD_PROPERTIES.MAX_INPUT,
          min: BUILD_PROPERTIES.MIN_INPUT
        }
      },
      [EParamsKeys.height]: {
        type: EParamType.NUMBER,
        label: EParamLabel.MODEL_HEIGHT,
        unit: EParamUnit.MM,
        settings: {
          readonly: false,
          max: BUILD_PROPERTIES.MAX_INPUT,
          min: BUILD_PROPERTIES.MIN_INPUT
        }
      },
      [EParamsKeys.rotation]: {
        type: EParamType.NUMBER,
        label: EParamLabel.PLANE_ROTATE,
        unit: EParamUnit.ANGLE,
        settings: {
          min: BUILD_PROPERTIES.ROTATION_ANGLE_RANGE[0],
          max: BUILD_PROPERTIES.ROTATION_ANGLE_RANGE[1],
          readonly: false
        }
      },
      [EParamsKeys.offGround]: {
        type: EParamType.NUMBER,
        label: EParamLabel.MODEL_OFFGROUND,
        unit: EParamUnit.MM,
        settings: {
          readonly: false,
          max: globalStores.paramStore.layerHeight,
          min: 0
        }
      },
      [EParamsKeys.furnitureInfo]: {
        type: EParamType.MATERIAL_INFO,
        label: EParamLabel.ROOM_TYPE,
        settings: {
          hideLabel: true,
          readonly: false
        }
      }
    }
  };
};
